/**
 * Sign-in screen.
 *
 * One field. Enter a work email, get a link, click it, you are in. The link
 * is requested for the dashboard specifically, so it comes back here and only
 * admits dashboard roles; the backend says why when it refuses, and this
 * screen shows that reason in plain words.
 *
 * Lab flow: when the backend reports demo auth on, a paste-token form appears
 * under the email form so a developer can skip the email round trip.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { apiHost } from '../api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  fetchAuthConfig,
  requestMagicLink,
  storeAuth,
  verifyMagicLink,
  type DeniedReason,
} from './auth';

interface Props {
  /** Optional pre-filled email. */
  defaultEmail?: string;
  /** Header shown above the form. */
  title?: string;
  /** A failure from a previous attempt (a bad paste token, a hydrate that failed). */
  error?: string;
  /** The backend refused the last email link and said why. */
  denied?: DeniedReason;
  /** Called after a successful verify so the host can re-render. */
  onSignedIn: () => void;
}

const DENIED_COPY: Record<DeniedReason, { title: string; body: string }> = {
  no_account: {
    title: 'No dashboard account for that email',
    body: 'The dashboard is for trainers, supervisors and admins. Ask an admin to add you under Admin, Users, then request a new link.',
  },
  no_access: {
    title: 'That account cannot use the dashboard',
    body: 'Your account is set up for the glasses and phone apps. Ask an admin to change your role if you need the dashboard.',
  },
  expired: {
    title: 'That link has expired or was already used',
    body: 'Links work once and for 15 minutes. Request a new one below.',
  },
};

export function SignIn({
  defaultEmail = '',
  title = 'Sign in to EON Field IQ',
  error: initialError,
  denied,
  onSignedIn,
}: Props) {
  const [email, setEmail] = useState(defaultEmail);
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'verifying'>('idle');
  const [error, setError] = useState<string | undefined>(initialError);
  const [demoEnabled, setDemoEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAuthConfig(apiHost)
      .then((cfg) => {
        if (!cancelled) setDemoEnabled(cfg.demoAuthEnabled);
      })
      .catch(() => {
        /* probe failure is non-fatal; default to demo off */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function sendLink(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    setStatus('sending');
    try {
      await requestMagicLink(apiHost, email.trim());
      setStatus('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('idle');
    }
  }

  async function signInWithToken(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    setStatus('verifying');
    try {
      const payload = await verifyMagicLink(apiHost, token.trim());
      storeAuth(payload);
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('idle');
    }
  }

  const deniedCopy = denied && status !== 'sent' ? DENIED_COPY[denied] : undefined;

  return (
    <div className="grid min-h-svh place-items-center bg-background p-4 sm:p-6">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <div className="mb-1 flex items-center gap-2.5">
          <span className="grid size-8 place-items-center rounded-tile bg-field text-on-field">
            <svg viewBox="0 0 24 24" className="size-[18px]" aria-hidden>
              <path
                d="M6 8h12M6 12h8M6 16h12"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </span>
          <span className="text-subtitle text-ink">EON Field IQ</span>
        </div>

        {status === 'sent' ? (
          <div
            data-testid="signin-sent"
            className="flex flex-col gap-2.5 rounded-panel border border-line bg-card p-5 shadow-panel"
          >
            <h2 className="text-title text-ink">Check your inbox</h2>
            <p className="text-support text-ink-dim">
              We sent a sign-in link to <strong className="text-ink">{email.trim()}</strong>. It
              works once and expires in 15 minutes.
            </p>
            <p className="text-meta text-ink-faint">
              Nothing there? Check spam, or make sure this address has a dashboard account.
            </p>
            <div>
              <Button variant="outline" size="sm" onClick={() => setStatus('idle')}>
                Use a different email
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={sendLink}
            className="flex flex-col gap-4 rounded-panel border border-line bg-card p-5 shadow-panel"
          >
            <div>
              <h2 className="text-title text-ink">{title}</h2>
              <p className="mt-1 text-support text-ink-dim">
                Enter your work email and we will send you a sign-in link.
              </p>
            </div>
            {deniedCopy ? (
              <div
                role="alert"
                className="rounded-control bg-error-soft px-3 py-2.5 text-support text-error"
              >
                <div className="font-semibold">{deniedCopy.title}</div>
                <div className="mt-0.5">{deniedCopy.body}</div>
              </div>
            ) : null}
            {error ? (
              <div
                role="alert"
                data-testid="signin-error"
                className="rounded-control bg-error-soft px-3 py-2.5 text-support text-error"
              >
                {error}
              </div>
            ) : null}
            <label className="flex flex-col gap-1.5">
              <span className="text-meta font-semibold text-ink-dim">Email</span>
              <Input
                data-testid="signin-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'sending'}
              />
            </label>
            <Button type="submit" data-testid="signin-submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send sign-in link'}
            </Button>
          </form>
        )}

        {demoEnabled && status !== 'sent' ? (
          <form
            data-testid="signin-demo-form"
            onSubmit={signInWithToken}
            className="flex flex-col gap-3 rounded-panel border border-line bg-card p-5 shadow-panel"
          >
            <div>
              <h3 className="text-subtitle text-ink">Paste a token</h3>
              <p className="text-meta text-ink-faint">
                Lab only. Paste the token from the backend log.
              </p>
            </div>
            <Input
              className="font-mono"
              placeholder="token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={status === 'verifying'}
            />
            <Button variant="outline" type="submit" disabled={status === 'verifying' || !token.trim()}>
              {status === 'verifying' ? 'Verifying…' : 'Sign in with token'}
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
